/**
 * The ops side of a booking — `/v1/admin/bookings*`.
 *
 * **The board filters on the server.** Every narrowing the screen offers — city, status, puja,
 * date window, `BMP…` code — is a query parameter, because the list is paged and a client-side
 * `.filter()` searches only the page in hand and then confidently reports no match.
 *
 * **The moves come from the server too.** `useBookingMoves` asks which transitions an admin may
 * actually fire from where the booking is standing, and the console renders those and nothing
 * else. A button assembled from a list written here is a button that 409s the day an edge changes
 * — and the version of that bug nobody notices is the button that quietly stopped being offered.
 *
 * **There is no dispatch here.** Waves, offers, manual matching and replacements belong to B23 and
 * B24, which do not exist yet. This module offers no hook that pretends otherwise.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type BookingDetail = components['schemas']['OpsBookingDetail'];
export type PrivateDetails = components['schemas']['PrivateDetailsResponse'];
export type BookingLineItem = components['schemas']['BookingLineItemRow'];
export type BookingParticipant = components['schemas']['ParticipantRow'];
export type BookingMove = components['schemas']['MoveRow'];
export type OpsTimelineRow = components['schemas']['OpsTimelineRow'];
type BookingBoardResponse = components['schemas']['BookingBoardResponse'];
/** One row of the board read — what both the table and the phase lanes render. */
export type BoardRow = BookingBoardResponse['items'][number];
export type BookingsByState = components['schemas']['BookingsByStateOut'];
type MovesResponse = components['schemas']['MovesResponse'];
type OpsTimelineResponse = components['schemas']['OpsTimelineResponse'];

export interface BoardQuery extends Record<string, unknown> {
  city_id?: number;
  status?: readonly string[];
  /**
   * The endpoint also takes `starts_from` and `puja_id`. Neither is here, because no screen asks
   * for either yet and a parameter no caller sets is a branch no test can honestly exercise.
   */
  starts_until?: string;
  q?: string;
  /** `page`/`page_size` is the admin pagination contract — offset pages, not cursors. */
  page: number;
  page_size: number;
}

/** Exported for its own test — the whole point is that nothing narrows on the client. */
export function boardSearch(params: BoardQuery): string {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('page_size', String(params.page_size));
  if (params.city_id !== undefined) search.set('city_id', String(params.city_id));
  // Repeated rather than comma-joined: the endpoint takes `status` many times, and a comma in a
  // single value would arrive as one status nothing matches.
  for (const status of params.status ?? []) search.append('status', status);
  if (params.starts_until) search.set('starts_until', params.starts_until);
  if (params.q) search.set('q', params.q);
  return search.toString();
}

export function useBookingBoard(params: BoardQuery, enabled = true, pollMs?: number) {
  return useQuery({
    queryKey: keys.bookings.board(params),
    queryFn: () =>
      authedClient.request<BookingBoardResponse>(`/v1/admin/bookings?${boardSearch(params)}`),
    enabled,
    // The board is a live surface (admin doc 08 §4): ops set the cadence, and none is no poll.
    refetchInterval: pollMs ?? false,
  });
}

/** Today's bookings per status, and how many fall in the next seven days. */
export function useBookingsByState() {
  return useQuery({
    queryKey: keys.bookings.byState(),
    queryFn: () => authedClient.request<BookingsByState>('/v1/admin/bookings/by-state'),
  });
}

export function useBooking(bookingId: number | null) {
  return useQuery({
    queryKey: keys.bookings.one(bookingId ?? 0),
    queryFn: () => authedClient.request<BookingDetail>(`/v1/admin/bookings/${bookingId}`),
    enabled: bookingId !== null,
  });
}

export type AdminCancellationQuote = components['schemas']['AdminCancellationQuote'];

/** What a devotee cancelling now would get back, under the policy version the booking pinned
 *  (CSR-169, doc 17 §2.3). A read: the console never raises the cancel from it. */
export function useAdminCancellationQuote(code: string) {
  return useQuery({
    queryKey: keys.bookings.cancellationQuote(code),
    queryFn: () =>
      authedClient.request<AdminCancellationQuote>(`/v1/admin/bookings/${code}/cancellation-quote`),
  });
}

export function useBookingTimeline(bookingId: number | null) {
  return useQuery({
    queryKey: keys.bookings.timeline(bookingId ?? 0),
    queryFn: () =>
      authedClient.request<OpsTimelineResponse>(`/v1/admin/bookings/${bookingId}/timeline`),
    enabled: bookingId !== null,
  });
}

/** What this booking may be moved to, from where it is now. The console offers exactly these. */
export function useBookingMoves(bookingId: number | null) {
  return useQuery({
    queryKey: keys.bookings.moves(bookingId ?? 0),
    queryFn: () => authedClient.request<MovesResponse>(`/v1/admin/bookings/${bookingId}/moves`),
    enabled: bookingId !== null,
  });
}

/**
 * Edge 45 — a superadmin moving a booking by hand, with a reason that is written down.
 *
 * Every booking read is invalidated afterwards, not just this booking's: the board's counts and
 * its filtered pages both change when a booking leaves the state they were selecting on.
 */
export function useForceBookingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      bookingId: number;
      to_status: string;
      trigger: string;
      reason: string;
    }) =>
      authedClient.request<BookingDetail>(`/v1/admin/bookings/${args.bookingId}/force-status`, {
        method: 'POST',
        body: { to_status: args.to_status, trigger: args.trigger, reason: args.reason },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.bookings.all() }),
  });
}

/**
 * The devotee's address and participants, which the board and the detail never carry (admin doc 08
 * §3.3 item 3). Refused outside the booking day; on it, every call is an audited access with the
 * operator's reason. Not cached: the answer is for the operator who asked, now.
 */
export function useRevealPrivate() {
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: number; reason: string }) =>
      authedClient.request<PrivateDetails>(`/v1/admin/bookings/${bookingId}/private/reveal`, {
        method: 'POST',
        body: { reason },
      }),
  });
}

export type AdminCancelResult = components['schemas']['AdminCancelResult'];

/**
 * Cancel by an admin — safety, fraud, force majeure or our own error (admin doc 08 §3.4).
 *
 * Always a full refund: the window table never applies, because in every one of those reasons the
 * devotee did nothing. Both the bookings and the refunds reads go stale, since the refund it
 * raises is a row the refunds screen lists.
 */
export function useAdminCancelBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { code: string; reason_code: string; memo: string }) =>
      authedClient.request<AdminCancelResult>(`/v1/admin/bookings/${args.code}/cancel`, {
        method: 'POST',
        body: { reason_code: args.reason_code, memo: args.memo },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.bookings.all() });
      queryClient.invalidateQueries({ queryKey: keys.refunds.all() });
    },
  });
}

export type CodeOverrideKind = 'start' | 'completion';

/**
 * Start or complete a booking without its OTP (doc 04-backend/31 §8) — the dead-phone case.
 * Both routes want a reason and a support ticket, and both are audited against the operator.
 */
export function useCodeOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { code: string; kind: CodeOverrideKind; reason: string; ticket_code: string }) =>
      authedClient.request<{ booking_status: string }>(
        `/v1/admin/bookings/${args.code}/${args.kind === 'start' ? 'verify-start' : 'verify-completion'}`,
        { method: 'POST', body: { reason: args.reason, ticket_code: args.ticket_code } },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.bookings.all() }),
  });
}
